import type { AuditEventSummary, IndividualProfileSummary, IndividualProfilesResponse, RecordHistoryResponse, ResidentLifecycleStatus } from "@rbim/shared";
import { ArrowLeft, CalendarDays, Edit3, History, MapPin, Search, UserRound, UsersRound } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { useAuth } from "../auth";
import { ApiRequestError, apiRequest } from "../api";
import { SupervisorDeletionOtpActions } from "../components/deletion-otp-actions";
import { Pagination } from "./household-profiles-page";

export function IndividualProfilesPage({
  enableUpdateActions = false,
  enableSupervisorOtpDeleteActions = false,
}: {
  enableUpdateActions?: boolean;
  enableSupervisorOtpDeleteActions?: boolean;
}) {
  const { user } = useAuth();
  const [response, setResponse] = useState<IndividualProfilesResponse | null>(null);
  const [draftSearch, setDraftSearch] = useState("");
  const [search, setSearch] = useState("");
  const [barangayId, setBarangayId] = useState("");
  const [sex, setSex] = useState("");
  const [residentType, setResidentType] = useState("");
  const [lifecycleStatus, setLifecycleStatus] = useState("");
  const [lifecycleMonth, setLifecycleMonth] = useState("");
  const [lifecycleCounts, setLifecycleCounts] = useState({ deceased: 0, transferred: 0 });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<IndividualProfileSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    const parameters = new URLSearchParams({ search, page: String(page), pageSize: "20" });
    if (barangayId) parameters.set("barangayId", barangayId);
    if (sex) parameters.set("sex", sex);
    if (residentType) parameters.set("residentType", residentType);
    if (lifecycleStatus) parameters.set("lifecycleStatus", lifecycleStatus);
    if (lifecycleMonth) parameters.set("lifecycleMonth", lifecycleMonth);
    setLoading(true);
    setError("");
    apiRequest<IndividualProfilesResponse>(`/profiles/individuals?${parameters}`)
      .then((data) => {
        if (!active) return;
        setResponse(data);
        setSelected((current) => current ? data.items.find((item) => item.id === current.id) ?? null : null);
      })
      .catch((requestError) => { if (active) setError(requestError instanceof ApiRequestError ? requestError.message : "Individual profiles could not be loaded."); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [barangayId, lifecycleMonth, lifecycleStatus, page, residentType, search, sex]);

  useEffect(() => {
    if (user?.scope.type !== "MUNICIPALITY_CITY") return;
    let active = true;

    const buildCountQuery = (status: "DECEASED" | "TRANSFERRED") => {
      const parameters = new URLSearchParams({
        search: "",
        page: "1",
        pageSize: "5",
        lifecycleStatus: status,
      });
      if (barangayId) parameters.set("barangayId", barangayId);
      if (lifecycleMonth) parameters.set("lifecycleMonth", lifecycleMonth);
      return parameters;
    };

    Promise.all([
      apiRequest<IndividualProfilesResponse>(`/profiles/individuals?${buildCountQuery("DECEASED")}`),
      apiRequest<IndividualProfilesResponse>(`/profiles/individuals?${buildCountQuery("TRANSFERRED")}`),
    ])
      .then(([deceased, transferred]) => {
        if (!active) return;
        setLifecycleCounts({
          deceased: deceased.totalItems,
          transferred: transferred.totalItems,
        });
      })
      .catch(() => {
        if (active) setLifecycleCounts({ deceased: 0, transferred: 0 });
      });

    return () => { active = false; };
  }, [barangayId, lifecycleMonth, user?.scope.type]);

  function submitSearch(event: FormEvent<HTMLFormElement>) { event.preventDefault(); setPage(1); setSearch(draftSearch.trim()); }
  const changeFilter = (setter: (value: string) => void) => (value: string) => { setter(value); setPage(1); };
  function updateResident(updated: IndividualProfileSummary) {
    setSelected(updated);
    setResponse((current) => current ? { ...current, items: current.items.map((item) => item.id === updated.id ? updated : item) } : current);
  }
  function removeResident(residentId: string) {
    setSelected(null);
    setResponse((current) => current ? {
      ...current,
      items: current.items.filter((item) => item.id !== residentId),
      totalItems: Math.max(0, current.totalItems - 1),
    } : current);
  }

  if (selected) {
    return <IndividualDesktopDetail
      resident={selected}
      enableUpdateActions={enableUpdateActions}
      enableSupervisorOtpDeleteActions={enableSupervisorOtpDeleteActions}
      onBack={() => setSelected(null)}
      onChanged={updateResident}
      onDeleted={() => removeResident(selected.id)}
    />;
  }

  return (
    <section className="profile-desktop-page">
      <header className="profile-desktop-header">
        <div className="profile-desktop-header-main">
          <span className="profile-desktop-header-icon"><UserRound /></span>
          <div>
            <span className="eyebrow">Profiles</span>
            <h1>Individual Profiles</h1>
            <p>Submitted RBIM resident records with demographic, household, migration, and lifecycle information.</p>
          </div>
        </div>
        <span className="profile-date-chip"><CalendarDays />{formatHeaderDate(new Date())}</span>
      </header>

      <form className="profile-desktop-filter-card" onSubmit={submitSearch}>
        <div className="profile-desktop-filter-grid individual">
          <label>
            <span>Search residents</span>
            <span className="profile-desktop-search"><Search /><input value={draftSearch} onChange={(event) => setDraftSearch(event.currentTarget.value)} placeholder="Search name, household, address, or occupation..." /></span>
          </label>
          <label><span>Barangay</span><select value={barangayId} onChange={(event) => changeFilter(setBarangayId)(event.currentTarget.value)}><option value="">All Barangays</option>{response?.barangays.map((barangay) => <option key={barangay.id} value={barangay.id}>{barangay.name}</option>)}</select></label>
          <label><span>Sex</span><select value={sex} onChange={(event) => changeFilter(setSex)(event.currentTarget.value)}><option value="">All</option><option>Male</option><option>Female</option></select></label>
          <label><span>Migration</span><select value={residentType} onChange={(event) => changeFilter(setResidentType)(event.currentTarget.value)}><option value="">All</option><option>Non-Migrant</option><option>Migrant</option><option>Transient</option></select></label>
          <label><span>Month</span><input type="month" value={lifecycleMonth} onChange={(event) => changeFilter(setLifecycleMonth)(event.currentTarget.value)} /></label>
        </div>
      </form>

      {error ? <div className="form-error" role="alert">{error}</div> : null}

      {user?.scope.type === "MUNICIPALITY_CITY" ? (
        <section className="lifecycle-quick-filters" aria-label="Monthly lifecycle summary">
          <div>
            <strong>Monthly Lifecycle Summary</strong>
            <span>Summary of deceased and transferred residents for the selected month.</span>
          </div>
          <button type="button" className={lifecycleStatus === "DECEASED" ? "primary-button" : "secondary-button"} onClick={() => { setLifecycleStatus("DECEASED"); setPage(1); }}>Deceased ({lifecycleCounts.deceased.toLocaleString()})</button>
          <button type="button" className={lifecycleStatus === "TRANSFERRED" ? "primary-button" : "secondary-button"} onClick={() => { setLifecycleStatus("TRANSFERRED"); setPage(1); }}><MapPin />Transferred ({lifecycleCounts.transferred.toLocaleString()})</button>
          {lifecycleStatus ? <button type="button" className="secondary-button" onClick={() => { setLifecycleStatus(""); setPage(1); }}>Show all</button> : null}
        </section>
      ) : null}

      <section className="profile-desktop-table-card">
        <header className="profile-desktop-table-heading">
          <div><h2>Resident Profiles</h2><p>{loading ? "Loading resident records" : `${response?.totalItems ?? 0} resident ${(response?.totalItems ?? 0) === 1 ? "record" : "records"}`}</p></div>
          <span className="status-pill"><UsersRound />{response?.totalItems.toLocaleString() ?? "—"} residents</span>
        </header>

        {loading ? <div className="profile-desktop-empty">Loading authorized resident records…</div> : response?.items.length ? (
          <div className="profile-desktop-table-wrap">
            <table className="profile-desktop-table">
              <thead><tr><th>Resident</th><th>Household</th><th>Sex / Age</th><th>Barangay</th><th>Migration</th><th>Lifecycle</th><th>Open</th></tr></thead>
              <tbody>
                {response.items.map((resident) => (
                  <tr key={resident.id} onDoubleClick={() => setSelected(resident)}>
                    <td><span className="profile-primary-text">{resident.fullName || "Unnamed resident"}</span><span className="profile-secondary-text">{resident.occupation || "Occupation not encoded"}</span></td>
                    <td>{resident.householdCode || "—"}</td>
                    <td>{resident.sex || "—"} / {resident.age ?? "—"}</td>
                    <td>{resident.barangayName}</td>
                    <td>{resident.residentType || "—"}</td>
                    <td><span className="badge available">{formatCode(resident.lifecycleStatus)}</span></td>
                    <td><button className="profile-open-button" type="button" onClick={() => setSelected(resident)}>Open</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="profile-desktop-empty">No residents match the current filters.</div>}

        <Pagination page={response?.page ?? 1} totalPages={response?.totalPages ?? 1} total={response?.totalItems ?? 0} onPage={setPage} />
      </section>
    </section>
  );
}

function IndividualDesktopDetail({ resident, enableUpdateActions, enableSupervisorOtpDeleteActions, onBack, onChanged, onDeleted }: {
  resident: IndividualProfileSummary;
  enableUpdateActions: boolean;
  enableSupervisorOtpDeleteActions: boolean;
  onBack(): void;
  onChanged(resident: IndividualProfileSummary): void;
  onDeleted(): void;
}) {
  return (
    <section className="profile-desktop-page">
      <section className="profile-desktop-detail-card">
        <div className="profile-detail-topbar">
          <div className="profile-detail-topbar-left">
            <button className="profile-detail-back" type="button" onClick={onBack} aria-label="Back to individual profiles"><ArrowLeft /></button>
            <div><span className="eyebrow">Individual profile</span><strong>{resident.fullName || "Unnamed resident"}</strong></div>
          </div>
        </div>

        <div className="profile-detail-hero">
          <div className="profile-detail-hero-main">
            <span className="profile-detail-hero-icon"><UserRound /></span>
            <div><span className="eyebrow">Resident profile</span><h2>{resident.fullName || "Unnamed resident"}</h2><p>{resident.barangayName} · Household {resident.householdCode || "—"}</p></div>
          </div>
          <span className="badge available">{formatCode(resident.lifecycleStatus)}</span>
        </div>

        <div className="profile-detail-grid">
          <Info label="Barangay" value={resident.barangayName} />
          <Info label="Household" value={resident.householdCode || "—"} />
          <Info label="Family unit" value={resident.familyUnitNo || "—"} />
          <Info label="Sex / age" value={`${resident.sex || "—"} / ${resident.age ?? "—"}`} />
          <Info label="Date of birth" value={resident.dateOfBirth || "—"} />
          <Info label="Civil status" value={resident.civilStatus || "—"} />
          <Info label="Migration classification" value={resident.residentType || "—"} />
          <Info label="Lifecycle status" value={formatCode(resident.lifecycleStatus)} />
          <Info label="Status date" value={resident.lifecycleStatusDate || "—"} />
          <Info label="Status remarks" value={resident.lifecycleReason || "—"} />
          <Info label="Movement destination" value={resident.lifecycleDestination || "—"} />
          <Info label="Occupation" value={resident.occupation || "—"} />
          <Info label="Disability" value={resident.disability || "—"} />
          <Info label="Senior citizen registration" value={resident.registeredSeniorCitizen || "—"} />
          <Info label="Address" value={resident.address || "—"} />
        </div>

        {enableUpdateActions || enableSupervisorOtpDeleteActions ? <div className="profile-actions-section"><ResidentUpdateActions key={resident.id} resident={resident} canUpdate={enableUpdateActions} canOtpDelete={enableSupervisorOtpDeleteActions} onChanged={onChanged} onDeleted={onDeleted} /></div> : null}
      </section>
    </section>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return <div className="profile-info-card"><span>{label}</span><strong>{value}</strong></div>;
}

function ResidentUpdateActions({ resident, canUpdate, canOtpDelete, onChanged, onDeleted }: {
  resident: IndividualProfileSummary;
  canUpdate: boolean;
  canOtpDelete: boolean;
  onChanged?(resident: IndividualProfileSummary): void;
  onDeleted(): void;
}) {
  const [editing, setEditing] = useState(false);
  const [newStatus, setNewStatus] = useState<ResidentLifecycleStatus>(resident.lifecycleStatus);
  const [changeDate, setChangeDate] = useState(todayLocal());
  const [reason, setReason] = useState("");
  const [destination, setDestination] = useState("");
  const [history, setHistory] = useState<AuditEventSummary[] | null>(null);
  const [lifecycleConfirmOpen, setLifecycleConfirmOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function saveInformation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setError(""); setMessage("");
    const form = new FormData(event.currentTarget);
    const changes = {
      firstName: String(form.get("firstName") ?? "").trim(), middleName: String(form.get("middleName") ?? "").trim(),
      lastName: String(form.get("lastName") ?? "").trim(), civilStatus: String(form.get("civilStatus") ?? "").trim(),
      occupation: String(form.get("occupation") ?? "").trim(), disability: String(form.get("disability") ?? "").trim(),
      registeredSeniorCitizen: String(form.get("registeredSeniorCitizen") ?? "").trim(),
    };
    if (!window.confirm(`Save permitted information changes for ${resident.fullName}?`)) return;
    try {
      await apiRequest(`/records/${resident.recordId}/individuals/${resident.memberIndex}`, { method: "PATCH", body: JSON.stringify(changes) });
      onChanged?.({ ...resident, ...changes, fullName: [changes.firstName, changes.middleName, changes.lastName].filter(Boolean).join(" "), updatedAt: new Date().toISOString() });
      setEditing(false); setMessage("Resident information updated and audit logged.");
    } catch (requestError) { setError(requestError instanceof ApiRequestError ? requestError.message : "Resident information could not be updated."); }
  }

  function reviewLifecycleChange(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setMessage("");
    if (newStatus === resident.lifecycleStatus) { setError("Choose a new lifecycle status."); return; }
    setLifecycleConfirmOpen(true);
  }

  async function saveLifecycle() {
    setError("");
    setMessage("");
    try {
      await apiRequest(`/records/${resident.recordId}/individuals/${resident.memberIndex}/status`, { method: "PATCH", body: JSON.stringify({ status: newStatus, changeDate, reason, destination }) });
      onChanged?.({ ...resident, lifecycleStatus: newStatus, lifecycleStatusDate: changeDate, lifecycleChangedAt: new Date().toISOString(), lifecycleReason: reason, lifecycleDestination: destination, updatedAt: new Date().toISOString() });
      setLifecycleConfirmOpen(false);
      setMessage(`Lifecycle status changed from ${formatCode(resident.lifecycleStatus)} to ${formatCode(newStatus)} and audit logged.`);
    } catch (requestError) { setError(requestError instanceof ApiRequestError ? requestError.message : "Lifecycle status could not be updated."); }
  }

  async function loadHistory() {
    setError("");
    try {
      const result = await apiRequest<RecordHistoryResponse>(`/records/${resident.recordId}/history`);
      setHistory(result.events.filter((event) => event.metadata.memberIndex === resident.memberIndex));
    } catch (requestError) { setError(requestError instanceof ApiRequestError ? requestError.message : "Record history could not be loaded."); }
  }

  return <section className="supervisor-resident-actions">
    <h3>Record actions</h3>
    {canUpdate ? <div className="record-action-row"><button className="secondary-button" type="button" onClick={() => setEditing((value) => !value)}><Edit3 />Edit</button><button className="secondary-button" type="button" onClick={() => void loadHistory()}><History />View History</button></div> : null}
    {canOtpDelete ? <SupervisorDeletionOtpActions
      targetType="INDIVIDUAL"
      recordId={resident.recordId}
      memberIndex={resident.memberIndex}
      label={resident.fullName || "this individual"}
      onDeleted={onDeleted}
    /> : null}
    {message ? <div className="success-message" role="status">{message}</div> : null}
    {error ? <div className="form-error" role="alert">{error}</div> : null}
    {canUpdate && editing ? <form className="resident-edit-form" onSubmit={saveInformation}>
      <label>First name<input name="firstName" defaultValue={resident.firstName} required /></label><label>Middle name<input name="middleName" defaultValue={resident.middleName} /></label><label>Last name<input name="lastName" defaultValue={resident.lastName} required /></label><label>Civil status<input name="civilStatus" defaultValue={resident.civilStatus} /></label><label>Occupation<input name="occupation" defaultValue={resident.occupation} /></label><label>Disability<input name="disability" defaultValue={resident.disability} /></label><label>Senior citizen registration<input name="registeredSeniorCitizen" defaultValue={resident.registeredSeniorCitizen} /></label><button className="primary-button">Save resident information</button>
    </form> : null}
    {canUpdate ? <form className="lifecycle-form" onSubmit={reviewLifecycleChange}><h4>Update Residency / Movement Status</h4><div className="record-action-row"><button className="danger-button" type="button" onClick={() => { setNewStatus("DECEASED"); setLifecycleConfirmOpen(false); }}>Mark as Deceased</button><button className="secondary-button" type="button" onClick={() => { setNewStatus("TRANSFERRED"); setLifecycleConfirmOpen(false); }}><MapPin />Transfer to Another Location/Barangay</button></div><div className="lifecycle-change-preview"><span>Current<strong>{formatCode(resident.lifecycleStatus)}</strong></span><span>New<strong>{formatCode(newStatus)}</strong></span></div><label>New status<select value={newStatus} onChange={(event) => { setNewStatus(event.currentTarget.value as ResidentLifecycleStatus); setLifecycleConfirmOpen(false); }}><option value="ACTIVE">Active</option><option value="DECEASED">Deceased</option><option value="MOVED_OUT">Moved Out</option><option value="TRANSFERRED">Transferred</option><option value="INACTIVE">Inactive</option></select></label><label>Change date<input type="date" value={changeDate} onChange={(event) => { setChangeDate(event.currentTarget.value); setLifecycleConfirmOpen(false); }} required /></label><label>Reason / remarks<textarea value={reason} onChange={(event) => { setReason(event.currentTarget.value); setLifecycleConfirmOpen(false); }} maxLength={1000} required={newStatus !== "ACTIVE"} /></label>{newStatus === "MOVED_OUT" || newStatus === "TRANSFERRED" ? <label>{newStatus === "TRANSFERRED" ? "Transfer destination / Barangay" : "Destination"}<input value={destination} onChange={(event) => { setDestination(event.currentTarget.value); setLifecycleConfirmOpen(false); }} placeholder={newStatus === "TRANSFERRED" ? "Enter destination barangay or location" : "Enter destination"} maxLength={1000} required /></label> : null}<button className={newStatus === "DECEASED" ? "danger-button" : "primary-button"}>{newStatus === "DECEASED" ? "Review Deceased Status" : "Review Status Change"}</button>{lifecycleConfirmOpen ? <div className={`lifecycle-confirmation-panel ${newStatus === "DECEASED" ? "is-deceased" : ""}`} role="alertdialog" aria-labelledby="lifecycle-confirmation-title"><div><span className="eyebrow">Final confirmation</span><h4 id="lifecycle-confirmation-title">{newStatus === "DECEASED" ? "Mark this individual as deceased?" : "Confirm residency / movement status change?"}</h4><p>The historical record will be retained and this change will be written to the RBIM audit trail.</p></div><div className="lifecycle-confirmation-grid"><div><span>Resident</span><strong>{resident.fullName}</strong></div><div><span>Current status</span><strong>{formatCode(resident.lifecycleStatus)}</strong></div><div><span>New status</span><strong>{formatCode(newStatus)}</strong></div><div><span>Change date</span><strong>{changeDate}</strong></div>{reason ? <div className="lifecycle-confirmation-wide"><span>Reason / remarks</span><strong>{reason}</strong></div> : null}{destination ? <div className="lifecycle-confirmation-wide"><span>Destination</span><strong>{destination}</strong></div> : null}</div><div className="lifecycle-confirmation-actions"><button type="button" className="secondary-button" onClick={() => setLifecycleConfirmOpen(false)}>Back</button><button type="button" className={newStatus === "DECEASED" ? "danger-button" : "primary-button"} onClick={() => void saveLifecycle()}>{newStatus === "DECEASED" ? "Confirm Deceased Status" : "Confirm Status Change"}</button></div></div> : null}</form> : null}
    {history ? <section className="record-history"><h4>Individual history</h4>{history.length ? <ol>{history.map((event) => <li key={event.id}><strong>{formatCode(event.action)}</strong><time>{new Intl.DateTimeFormat("en-PH", { dateStyle: "medium", timeStyle: "short" }).format(new Date(event.createdAt))}</time></li>)}</ol> : <p className="muted-copy">No individual changes have been recorded.</p>}</section> : null}
  </section>;
}

function todayLocal() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function formatHeaderDate(value: Date) {
  return new Intl.DateTimeFormat("en-PH", { month: "short", day: "numeric", year: "numeric" }).format(value);
}

function formatCode(value: string) {
  return value.replaceAll("_", " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
}



